import { twMerge } from "tailwind-merge";
import { Button, Card, H1, H2 } from "@sellar-io/components";
import {
  StorefrontTagListResponseType,
  SupplierGetResponseType,
} from "@sellar-io/api-v2/types";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { Fragment, ReactElement } from "react";
import { useShallow } from "zustand/react/shallow";
import { Icon, BuyerOnly, SpinnerLegacy } from "../../../../../components";
import { StorefrontContactSupplierBanner } from "../StorefrontContactSupplierBanner/StorefrontContactSupplierBanner";
import { useStorefrontFilterStore } from "../../../StorefrontFilterStore";
import { PoweredBySellar } from "../PoweredBySellar";
import { StorefrontParentProductList } from "../StorefrontParentProductList";
import { SignUpToAccessBanner } from "../SignUpBanners/SignUpToAccessBanner";
import { useStockListContext } from "../../../useStockListContext";
import {
  showProductsWithoutTags,
  useCompletedTagQueryList,
} from "./StorefrontProductTagsContent.shared";

export const StorefrontProductTagsContent = ({
  tagsQuery,
  supplier,
  selectedGroupIds,
}: {
  tagsQuery: UseInfiniteQueryResult<
    InfiniteData<StorefrontTagListResponseType>,
    Error
  >;
  supplier: SupplierGetResponseType | undefined;
  selectedGroupIds?: number[];
}): ReactElement | null => {
  const { completedQueryList, setCompletedQueryList } =
    useCompletedTagQueryList(tagsQuery.data?.pages[0]);
  const { supplierStorefront } = useStockListContext();

  if (!supplier) return null;

  return (
    <div className={"flex flex-col gap-2"}>
      {tagsQuery.isLoading && tagsQuery.fetchStatus !== "idle" ? (
        <SpinnerLegacy />
      ) : (
        <>
          {!!completedQueryList.length && (
            <>
              {tagsQuery.data?.pages[0]?.map((tag, index) => (
                <Fragment key={tag.id}>
                  {index === 1 &&
                    supplierStorefront?.storefrontLinkType === "noPricing" && (
                      <SignUpToAccessBanner supplier={supplier} />
                    )}
                  {(index > 0 && completedQueryList[index - 1]) ||
                  index === 0 ? (
                    <>
                      <H1 className={"pt-8 pb-4"}>{tag.value}</H1>
                      <StorefrontParentProductList
                        key={index}
                        currentIndex={index}
                        StorefrontTagId={tag.id}
                        completedQueryList={completedQueryList}
                        setCompletedQueryList={setCompletedQueryList}
                        supplierId={supplier.id}
                      />
                    </>
                  ) : (
                    <>{completedQueryList[index - 1] && <SpinnerLegacy />}</>
                  )}
                </Fragment>
              ))}
            </>
          )}
          {showProductsWithoutTags({
            tagsQuery,
            completedQueryList,
            selectedGroupIds,
          }) && <StorefrontParentProductList supplierId={supplier.id} />}

          <div className={"mt-6 mb-8 flex flex-col gap-4"}>
            <BuyerOnly>
              {!tagsQuery.isPending && (
                <>
                  <FiltersAppliedCard />
                  <StorefrontContactSupplierBanner supplier={supplier} />
                </>
              )}
            </BuyerOnly>
            <PoweredBySellar />
          </div>
        </>
      )}
    </div>
  );
};

const FiltersAppliedCard = () => {
  const state = useStorefrontFilterStore(useShallow((state) => state));
  if (!state.filtersAppliedFromMarket) return null;
  return (
    <Card
      className={twMerge("bg-white", "border border-zinc-300 bg-zinc-50 p-4")}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-2">
          <div className="relative h-12 w-12 rounded-3xl bg-zinc-200">
            <div className="absolute top-3.5 left-3.5 h-6 w-6 overflow-hidden">
              <Icon icon={"EYE_OFF"} />
            </div>
          </div>
          <H2>See all products</H2>
        </div>
        <Button color={"slim"} onClick={state.reset}>
          Clear filters <Icon icon={"CLOSE"} />
        </Button>
      </div>
    </Card>
  );
};
